/**
 * InboxFiltersBar
 * Clean, minimal filter bar with elegant typography.
 */

import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

import { PACKAGE_TYPE_CONFIG, STATUS_FILTER_OPTIONS } from '../constants';
import type {
  CorrespondencePackageType,
  InboxFilters as Filters,
  UnifiedStatus,
} from '../types';

interface InboxFiltersProps {
  filters: Filters;
  onSearchChange: (search: string) => void;
  onTypeChange: (type: CorrespondencePackageType | 'all') => void;
  onStatusChange: (status: UnifiedStatus | 'all') => void;
  onClear: () => void;
  hasActiveFilters: boolean;
  resultCount: number;
}

export const InboxFiltersBar: React.FC<InboxFiltersProps> = ({
  filters,
  onSearchChange,
  onTypeChange,
  onStatusChange,
  onClear,
  hasActiveFilters,
  resultCount,
}) => (
  <div className="flex items-center gap-2">
    {/* Search */}
    <div className="relative flex-1 max-w-xs group">
      <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400 group-focus-within:text-stone-600 transition-colors" />
      <input
        type="text"
        value={filters.search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search..."
        className={cn(
          'w-full h-9 pl-9 pr-8 text-sm rounded-lg',
          'bg-stone-100/60 border border-transparent',
          'focus:outline-none focus:bg-white focus:border-stone-200 focus:shadow-sm',
          'placeholder:text-stone-400 transition-all duration-150',
        )}
      />
      {filters.search && (
        <button
          type="button"
          onClick={() => onSearchChange('')}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded text-stone-400 hover:text-stone-600 hover:bg-stone-200/60 transition-all"
        >
          <XMarkIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </div>

    {/* Type Filter */}
    <FilterPill
      label="Type"
      value={
        filters.packageType === 'all'
          ? null
          : PACKAGE_TYPE_CONFIG[filters.packageType]?.shortLabel
      }
      isActive={filters.packageType !== 'all'}
    >
      <DropdownMenuItem
        onClick={() => onTypeChange('all')}
        className={cn(
          'cursor-pointer rounded py-1.5 text-sm',
          filters.packageType === 'all' && 'bg-stone-100 font-medium',
        )}
      >
        All types
      </DropdownMenuItem>
      {Object.entries(PACKAGE_TYPE_CONFIG).map(([type, config]) => (
        <DropdownMenuItem
          key={type}
          onClick={() => onTypeChange(type as CorrespondencePackageType)}
          className={cn(
            'cursor-pointer rounded py-1.5 text-sm',
            filters.packageType === type && 'bg-stone-100 font-medium',
          )}
        >
          <span
            className={cn('h-1.5 w-1.5 rounded-full mr-2', config.dotColor)}
          />
          {config.label}
        </DropdownMenuItem>
      ))}
    </FilterPill>

    {/* Status Filter */}
    <FilterPill
      label="Status"
      value={
        filters.status === 'all'
          ? null
          : (STATUS_FILTER_OPTIONS.find((o) => o.value === filters.status)
              ?.label ?? null)
      }
      isActive={filters.status !== 'all'}
    >
      <DropdownMenuItem
        onClick={() => onStatusChange('all')}
        className={cn(
          'cursor-pointer rounded py-1.5 text-sm',
          filters.status === 'all' && 'bg-stone-100 font-medium',
        )}
      >
        All statuses
      </DropdownMenuItem>
      {STATUS_FILTER_OPTIONS.map((option) => (
        <DropdownMenuItem
          key={option.value}
          onClick={() => onStatusChange(option.value as UnifiedStatus)}
          className={cn(
            'cursor-pointer rounded py-1.5 text-sm',
            filters.status === option.value && 'bg-stone-100 font-medium',
          )}
        >
          {option.label}
        </DropdownMenuItem>
      ))}
    </FilterPill>

    {/* Clear */}
    {hasActiveFilters && (
      <button
        type="button"
        onClick={onClear}
        className="text-xs text-stone-500 hover:text-stone-700 px-2 py-1 hover:bg-stone-100 rounded transition-colors"
      >
        Clear
      </button>
    )}

    {/* Count */}
    <span className="ml-auto text-xs text-stone-400 tabular-nums">
      {resultCount}
    </span>
  </div>
);

// ============================================================================
// Filter Pill
// ============================================================================

interface FilterPillProps {
  label: string;
  value: string | null;
  isActive: boolean;
  children: React.ReactNode;
}

const FilterPill: React.FC<FilterPillProps> = ({
  label,
  value,
  isActive,
  children,
}) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        variant="ghost"
        size="sm"
        className={cn(
          'h-9 px-3 gap-1 rounded-lg text-sm font-normal',
          'hover:bg-stone-100 transition-colors',
          isActive && 'bg-stone-900 text-white hover:bg-stone-800',
        )}
      >
        {value ?? label}
        <ChevronDownIcon className="h-4 w-4 opacity-50" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="start" className="w-44 p-1">
      {children}
    </DropdownMenuContent>
  </DropdownMenu>
);
